import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import UnderlineExt from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Bold, Italic, Underline, Heading2, List, ListOrdered } from "lucide-react";
import { plainToHtml, sanitizeRichHtml, isRichHtml } from "@/lib/richText";
import { cn } from "@/lib/utils";

interface RichTextFieldProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  id?: string;
}

/** Lightweight rich text editor for itinerary descriptions (bold, italic, underline, heading, lists). */
export function RichTextField({ value, onChange, placeholder, id }: RichTextFieldProps) {
  const lastEmitted = useRef<string>(value);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2] }, codeBlock: false, blockquote: false, code: false, horizontalRule: false }),
      UnderlineExt,
      Placeholder.configure({ placeholder: placeholder || "Describe this item..." }),
    ],
    content: plainToHtml(value),
    onUpdate: ({ editor: ed }) => {
      const html = ed.isEmpty ? "" : ed.getHTML();
      lastEmitted.current = html;
      onChange(html);
    },
  });

  // Load new content when the dialog switches to a different item.
  useEffect(() => {
    if (!editor || value === lastEmitted.current) return;
    lastEmitted.current = value;
    editor.commands.setContent(plainToHtml(value), { emitUpdate: false } as any);
  }, [value, editor]);

  const btn = (active: boolean, onClick: () => void, title: string, Icon: any) => (
    <Button type="button" size="icon" variant={active ? "default" : "ghost"} className="h-7 w-7 p-0" onClick={onClick} title={title}>
      <Icon className="h-3.5 w-3.5" />
    </Button>
  );

  return (
    <div id={id} className="rounded-md border border-input bg-background">
      {editor && (
        <div className="flex items-center gap-0.5 flex-wrap px-2 py-1 border-b bg-muted/30">
          {btn(editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), "Bold", Bold)}
          {btn(editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), "Italic", Italic)}
          {btn(editor.isActive("underline"), () => editor.chain().focus().toggleUnderline().run(), "Underline", Underline)}
          <Separator orientation="vertical" className="h-5 mx-1" />
          {btn(editor.isActive("heading", { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), "Heading", Heading2)}
          {btn(editor.isActive("bulletList"), () => editor.chain().focus().toggleBulletList().run(), "Bullet list", List)}
          {btn(editor.isActive("orderedList"), () => editor.chain().focus().toggleOrderedList().run(), "Numbered list", ListOrdered)}
        </div>
      )}
      <div className="px-3 py-2">
        <EditorContent editor={editor} className="prose-editor rich-text min-h-[90px] text-sm" />
      </div>
    </div>
  );
}

/** Safe display of a description that may be rich HTML or legacy plain text. */
export function RichTextDisplay({ value, className }: { value: string | null | undefined; className?: string }) {
  if (!value) return null;
  if (!isRichHtml(value)) return <p className={cn("whitespace-pre-wrap", className)}>{value}</p>;
  return <div className={cn("rich-text", className)} dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(value) }} />;
}
